// Console D's sidebar: the mark (and EXAMPLE DATA in the sandbox), then the console's navigation
// in D's order with only the pages that exist, or, on a settings page, the settings navigation
// with a way back. The foot holds what is left to set up, the plan and the person. One row is
// current at a time (lib/nav.ts).
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  appUserPinsQuery,
  billingQuery,
  meQuery,
  missingSymbolsQuery,
  projectsQuery,
  usageQuery,
  windowReleasesQuery,
} from '../../api/queries'
import { type MessageId, useI18n } from '../../i18n'
import { isSettingsKey, type NavKey, personHash } from '../../lib/nav'
import { useNavCounts } from '../../lib/navCounts'
import { releasesNewestFirst } from '../../lib/releases'
import { lowerFirst, SETUP_STEPS, STEP_TITLE, useSetupProgress } from '../../lib/setup'
import { useProjectParams } from '../../routes/params'
import type { ScopeSearch, SettingsSection } from '../../routes/search'
import { sandboxFor } from '../../sandbox'
import { Tag } from '../d'
import { Icon, type IconName } from '../icons'
import { Mark } from '../Mark'
import { useHiddenProjectName } from '../ProjectDeletions'
import { TrialMeter } from '../TrialMeter'
import { AccountMenu } from './AccountMenu'

type ProjectPath =
  | '/o/$orgId/p/$projectId/home'
  | '/o/$orgId/p/$projectId/agents'
  | '/o/$orgId/p/$projectId/instrumentation'
  | '/o/$orgId/p/$projectId/signals'
  | '/o/$orgId/p/$projectId/issues'
  | '/o/$orgId/p/$projectId/releases'
  | '/o/$orgId/p/$projectId/health'
  | '/o/$orgId/p/$projectId/performance'
  | '/o/$orgId/p/$projectId/performance/start'
  | '/o/$orgId/p/$projectId/performance/screens'
  | '/o/$orgId/p/$projectId/performance/network'
  | '/o/$orgId/p/$projectId/performance/hangs'
  | '/o/$orgId/p/$projectId/performance/frames'
  | '/o/$orgId/p/$projectId/performance/resources'
  | '/o/$orgId/p/$projectId/users'
  | '/o/$orgId/p/$projectId/sessions'
  | '/o/$orgId/p/$projectId/journeys'
  | '/o/$orgId/p/$projectId/explore'
  | '/o/$orgId/p/$projectId/logs'
  | '/o/$orgId/p/$projectId/dashboards'

/** The pages under Performance, in the sidebar's tree and the command bar. */
export const PERF_PAGES: readonly { key: NavKey; to: ProjectPath; label: MessageId }[] = [
  { key: 'perf.start', to: '/o/$orgId/p/$projectId/performance/start', label: 'perf.nav.start' },
  {
    key: 'perf.screens',
    to: '/o/$orgId/p/$projectId/performance/screens',
    label: 'perf.nav.screens',
  },
  {
    key: 'perf.network',
    to: '/o/$orgId/p/$projectId/performance/network',
    label: 'perf.nav.network',
  },
  { key: 'perf.hangs', to: '/o/$orgId/p/$projectId/performance/hangs', label: 'perf.nav.hangs' },
  {
    key: 'perf.frames',
    to: '/o/$orgId/p/$projectId/performance/frames',
    label: 'perf.nav.frames',
  },
  {
    key: 'perf.resources',
    to: '/o/$orgId/p/$projectId/performance/resources',
    label: 'perf.nav.resources',
  },
]

const SETTINGS_GROUPS: readonly {
  group: 'project' | 'organization' | 'account'
  items: readonly { section: SettingsSection; icon: IconName; label: MessageId }[]
}[] = [
  {
    group: 'project',
    items: [
      { section: 'general', icon: 'sliders', label: 'settings.tab.general' },
      { section: 'apps', icon: 'key', label: 'settings.tab.apps' },
      { section: 'symbols', icon: 'file', label: 'settings.tab.symbols' },
      { section: 'grouping', icon: 'stack', label: 'settings.tab.grouping' },
      { section: 'apm', icon: 'link', label: 'settings.tab.apm' },
      { section: 'agents', icon: 'agent', label: 'settings.tab.agents' },
      { section: 'data', icon: 'download', label: 'settings.tab.data' },
    ],
  },
  {
    group: 'organization',
    items: [
      { section: 'alerts', icon: 'alerts', label: 'settings.tab.alerts' },
      { section: 'members', icon: 'people', label: 'settings.tab.members' },
      { section: 'integrations', icon: 'plug', label: 'settings.tab.integrations' },
      { section: 'plan', icon: 'card', label: 'settings.tab.plan' },
      { section: 'activity', icon: 'history', label: 'settings.tab.activity' },
      { section: 'privacy', icon: 'shield', label: 'settings.tab.privacy' },
    ],
  },
  {
    group: 'account',
    items: [
      { section: 'profile', icon: 'user', label: 'settings.tab.profile' },
      { section: 'preferences', icon: 'sliders', label: 'settings.tab.preferences' },
    ],
  },
]

export function Sidebar({
  current,
  scope,
  open,
  backTo,
  onKeys,
}: {
  current: NavKey | undefined
  scope: ScopeSearch
  /** The drawer is open (a phone). */
  open: boolean
  /** Where "Back to the console" goes: the last console page, else Home. */
  backTo: string | undefined
  onKeys: () => void
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const sandbox = sandboxFor(projectId)
  const settings = isSettingsKey(current)
  return (
    <aside
      className={open ? 'nav open' : 'nav'}
      id="nav"
      aria-label={t('shell.sidebar')}
      data-mode={settings ? 'settings' : 'main'}
    >
      <div className="nav-top">
        <Link
          to="/o/$orgId/p/$projectId/home"
          params={{ orgId, projectId }}
          search={scope}
          className="nav-brand"
          aria-label={t('shell.home')}
        >
          <Mark />
          <span translate="no">apsio</span>
        </Link>
        {sandbox ? <Tag title={t('sandbox.footer')}>{t('sandbox.exampleData')}</Tag> : null}
      </div>
      {settings ? (
        <SettingsNav current={current} backTo={backTo} />
      ) : (
        <ConsoleNav current={current} scope={scope} />
      )}
      <Foot current={current} onKeys={onKeys} />
    </aside>
  )
}

/** A row: icon, label, and a count when something needs you. */
function Row({
  icon,
  label,
  here,
  count,
  children,
}: {
  icon: IconName
  label: string
  here: boolean
  count?: ReactNode
  children: (props: {
    className: string
    'aria-current': 'page' | undefined
    title: string
    children: ReactNode
  }) => ReactNode
}) {
  return children({
    className: 'nav-a',
    'aria-current': here ? 'page' : undefined,
    title: label,
    children: (
      <>
        <Icon name={icon} />
        <span className="lbl">{label}</span>
        {count}
      </>
    ),
  })
}

/** How many of the team's pins the sidebar lists; People lists them all. */
const PINNED_IN_NAV = 5

function ConsoleNav({ current, scope }: { current: NavKey | undefined; scope: ScopeSearch }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const sandbox = sandboxFor(projectId)
  const counts = useNavCounts(orgId, projectId, scope)
  const params = { orgId, projectId }
  const inPerf = current === 'perf' || !!current?.startsWith('perf.')
  // The performance pages fold by hand outside their section; inside it they stay open.
  const [perfKept, setPerfKept] = useState(false)
  const perfOpen = inPerf || perfKept
  // The people the team pinned (D114 #6): the first few, under Pinned; the open one is current
  // there instead of People. The sandbox cannot read pins (accounts API).
  const pins = useQuery({ ...appUserPinsQuery(orgId, projectId), enabled: !sandbox })
  // The shell's params stop at the project: the person comes from the path.
  const { pathname } = useLocation()
  const base = `/o/${orgId}/p/${projectId}`
  const userHash = pathname.startsWith(base) ? personHash(pathname.slice(base.length)) : undefined
  const pinnedRows = (pins.data?.pins ?? []).slice(0, PINNED_IN_NAV)
  const herePin = current === 'users' ? pinnedRows.find((p) => p.user_hash === userHash) : undefined
  // Pinned is last, as in the mock, so a short window hides it: the current pinned row is brought
  // into the nav's own view, never by scrolling the page (Mosaic, APS-421).
  const herePinRow = useRef<HTMLAnchorElement>(null)
  useEffect(() => {
    if (!herePin) return
    const row = herePinRow.current
    const scroller = row?.closest('.nav-main')
    if (!row || !(scroller instanceof HTMLElement)) return
    const reveal = () => {
      const r = row.getBoundingClientRect()
      const box = scroller.getBoundingClientRect()
      // The scroller's own bottom padding holds the focus ring: the row is in view above it.
      const bottom = box.bottom - Number.parseFloat(getComputedStyle(scroller).paddingBottom || '0')
      if (r.bottom > bottom) scroller.scrollTop += r.bottom - bottom
      else if (r.top < box.top) scroller.scrollTop -= box.top - r.top
    }
    reveal()
    // The foot loads after the rows (setup, plan) and shortens the scroller: look again then.
    if (typeof ResizeObserver !== 'function') return
    const watch = new ResizeObserver(reveal)
    watch.observe(scroller)
    return () => watch.disconnect()
  }, [herePin])
  const link = (to: ProjectPath, key: NavKey, icon: IconName, label: string, count?: ReactNode) => (
    <Row key={key} icon={icon} label={label} here={current === key} count={count}>
      {(p) => <Link to={to} params={params} search={scope} {...p} />}
    </Row>
  )
  return (
    <nav className="nav-main" aria-label={t('shell.nav')}>
      {link('/o/$orgId/p/$projectId/home', 'home', 'home', t('nav.home'))}
      {/* In the sandbox, Agents gives its token for the CLI and MCP (D105). */}
      {link('/o/$orgId/p/$projectId/agents', 'agents', 'agent', t('nav.agents'))}
      {/* What the SDK sends (D111): the policy, which the sandbox cannot change. */}
      {sandbox ? null : (
        <>
          <p className="nav-g">{t('shell.group.sdk')}</p>
          {link(
            '/o/$orgId/p/$projectId/instrumentation',
            'instrumentation',
            'toggle',
            t('nav.instrumentation'),
          )}
          {link('/o/$orgId/p/$projectId/signals', 'signals', 'capture', t('nav.signals'))}
        </>
      )}
      <p className="nav-g">{t('shell.group.monitor')}</p>
      {link(
        '/o/$orgId/p/$projectId/issues',
        'issues',
        'issues',
        t('nav.issues'),
        // The mock's "2 new": the issues new in the release, else the unresolved ones.
        counts.fresh?.count ? (
          <span className="cnt">
            <span aria-hidden="true">{t('nav.count.newShort', { count: counts.fresh.count })}</span>
            <span className="vh">
              {t('nav.count.newIn', { count: counts.fresh.count, release: counts.fresh.release })}
            </span>
          </span>
        ) : counts.issues ? (
          <span className="cnt">
            <span aria-hidden="true">{counts.issues}</span>
            <span className="vh">{t('nav.count.unresolved', { count: counts.issues })}</span>
          </span>
        ) : null,
      )}
      {sandbox ? null : (
        <Row
          icon="alerts"
          label={t('nav.alerts')}
          here={current === 'alerts'}
          count={
            counts.alerts ? (
              <span className="cnt hot">
                {t('nav.count.firingShort', { count: counts.alerts })}
              </span>
            ) : null
          }
        >
          {(p) => <Link to="/o/$orgId/p/$projectId/alerts" params={params} {...p} />}
        </Row>
      )}
      {link('/o/$orgId/p/$projectId/releases', 'releases', 'releases', t('nav.releases'))}
      {link('/o/$orgId/p/$projectId/health', 'health', 'pulse', t('nav.health'))}
      <div className={['nav-tree', perfOpen ? 'open' : '', inPerf ? 'in' : ''].join(' ').trim()}>
        {link('/o/$orgId/p/$projectId/performance', 'perf', 'perf', t('nav.performance'))}
        <button
          type="button"
          className="nav-tw"
          aria-expanded={perfOpen}
          aria-controls="nav-perf"
          aria-label={t('shell.perfPages')}
          onClick={() => setPerfKept(!perfOpen)}
          disabled={inPerf}
        >
          <Icon name="chev" small />
        </button>
        <div className="nav-sub" id="nav-perf">
          {PERF_PAGES.map((p) => (
            <Link
              key={p.key}
              to={p.to}
              params={params}
              search={scope}
              aria-current={current === p.key ? 'page' : undefined}
            >
              {t(p.label)}
            </Link>
          ))}
        </div>
      </div>
      <p className="nav-g">{t('shell.group.users')}</p>
      <Row icon="people" label={t('nav.people')} here={current === 'users' && !herePin}>
        {(p) => (
          // Exact: the router would mark People active under a pinned person too (one current row).
          <Link
            to="/o/$orgId/p/$projectId/users"
            params={params}
            search={scope}
            activeOptions={{ exact: true, includeSearch: false }}
            {...p}
          />
        )}
      </Row>
      {link('/o/$orgId/p/$projectId/sessions', 'sessions', 'sessions', t('nav.sessions'))}
      {link('/o/$orgId/p/$projectId/journeys', 'journeys', 'journeys', t('nav.journeys'))}
      <p className="nav-g">{t('shell.group.analyze')}</p>
      {/* run_query and dashboards: none from the sandbox (D95, D98); its logs read as anywhere. */}
      {sandbox
        ? null
        : link('/o/$orgId/p/$projectId/explore', 'explore', 'explore', t('nav.explore'))}
      {link('/o/$orgId/p/$projectId/logs', 'logs', 'logs', t('nav.logs'))}
      {sandbox
        ? null
        : link('/o/$orgId/p/$projectId/dashboards', 'dashboards', 'dash', t('nav.dashboards'))}
      {pinnedRows.length ? (
        <>
          <p className="nav-g">{t('shell.group.pinned')}</p>
          {pinnedRows.map((pin) => (
            <Row key={pin.id} icon="user" label={pin.label} here={herePin?.id === pin.id}>
              {(p) => (
                <Link
                  to="/o/$orgId/p/$projectId/users/$userHash"
                  params={{ ...params, userHash: pin.user_hash }}
                  search={scope}
                  ref={herePin?.id === pin.id ? herePinRow : undefined}
                  {...p}
                />
              )}
            </Row>
          ))}
        </>
      ) : null}
    </nav>
  )
}

function SettingsNav({
  current,
  backTo,
}: {
  current: NavKey | undefined
  backTo?: string | undefined
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const me = useQuery(meQuery)
  const projects = useQuery(projectsQuery(orgId))
  const org = me.data?.orgs.find((o) => o.id === orgId)
  const project = projects.data?.find((p) => p.id === projectId)
  // A project being deleted is in no list: its name comes with its deletion (APS-401).
  const hidden = useHiddenProjectName(orgId, projectId, !projects.data || !!project)
  const missing = useMissingSymbols(projectId)
  const home = `/o/${orgId}/p/${projectId}/home`
  const names: Record<'project' | 'organization' | 'account', string | undefined> = {
    project: project?.name ?? hidden,
    organization: org?.name,
    account: t('settings.group.you'),
  }
  return (
    <nav className="nav-set" aria-label={t('nav.settings')}>
      <a
        className="nav-a nav-back"
        href={backTo ?? home}
        title={t('shell.back')}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
          e.preventDefault()
          navigate({ href: backTo ?? home })
        }}
      >
        <Icon name="left" />
        <span className="lbl">{t('shell.back')}</span>
      </a>
      {SETTINGS_GROUPS.map((g) => (
        <div key={g.group} style={{ display: 'contents' }}>
          <p className="nav-g">
            {t(`settings.group.${g.group}`)}
            {names[g.group] ? <span>{names[g.group]}</span> : null}
          </p>
          {g.items.map((item) => (
            <Row
              key={item.section}
              icon={item.icon}
              label={t(item.label)}
              here={current === `settings.${item.section}`}
              count={
                item.section === 'symbols' && missing ? (
                  <span className="cnt warn">
                    <span aria-hidden="true">{`! ${missing}`}</span>
                    <span className="vh">{t('nav.count.missingSymbols', { count: missing })}</span>
                  </span>
                ) : null
              }
            >
              {(p) => (
                <Link
                  to="/o/$orgId/p/$projectId/settings/$section"
                  params={{ orgId, projectId, section: item.section }}
                  {...p}
                />
              )}
            </Row>
          ))}
        </div>
      ))}
    </nav>
  )
}

/** In-app images of the newest release without symbols (Settings › Symbols' first view). */
function useMissingSymbols(projectId: string): number | undefined {
  const now = Math.floor(Date.now() / 3_600_000) * 3_600_000
  const releases = useQuery(windowReleasesQuery(projectId, 30, now))
  const newest = releases.data ? releasesNewestFirst(releases.data)[0]?.release : undefined
  const missing = useQuery({
    ...missingSymbolsQuery(projectId, newest ?? ''),
    enabled: !!newest,
  })
  return missing.data?.missing.length || undefined
}

function Foot({ current, onKeys }: { current: NavKey | undefined; onKeys: () => void }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const sandbox = sandboxFor(projectId)
  const me = useQuery(meQuery)
  const usage = useQuery({ ...usageQuery(orgId), enabled: !sandbox })
  // Read here so the shell's banners and the plan share it.
  useQuery({ ...billingQuery(orgId), enabled: !sandbox })
  const setup = useSetupProgress(orgId, projectId)
  const org = me.data?.orgs.find((o) => o.id === orgId)
  const settings = isSettingsKey(current)
  const showSetup = !settings && setup.done !== undefined && setup.done < SETUP_STEPS
  const arc = 50.3
  return (
    <div className="nav-foot">
      {showSetup ? (
        <Link
          className="nav-setup"
          to="/o/$orgId/p/$projectId/setup"
          params={{ orgId, projectId }}
          aria-current={current === 'setup' ? 'page' : undefined}
          title={t('setup.title')}
        >
          <svg className="ring" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="10" r="8" />
            <circle
              className="v"
              cx="10"
              cy="10"
              r="8"
              strokeDasharray={`${(arc * (setup.done ?? 0)) / SETUP_STEPS} ${arc}`}
            />
          </svg>
          <span className="tx">
            <b>
              {t('setup.title')}{' '}
              <span>{t('setup.count', { done: setup.done ?? 0, total: SETUP_STEPS })}</span>
            </b>
            {setup.next ? (
              <span>{t('setup.next', { step: lowerFirst(t(STEP_TITLE[setup.next])) })}</span>
            ) : null}
          </span>
        </Link>
      ) : null}
      {sandbox ? (
        <div className="nav-meter">
          <span>{t('sandbox.footer')}</span>
          <SandboxExit />
        </div>
      ) : null}
      {org && !settings ? <TrialMeter org={org} usage={usage.data} projectId={projectId} /> : null}
      {me.data ? (
        <AccountMenu me={me.data} orgId={orgId} projectId={projectId} onKeys={onKeys} />
      ) : null}
    </div>
  )
}

/** Out of the sandbox, back to one's own projects (D105: only signed-in people are in it). */
function SandboxExit() {
  const { t } = useI18n()
  return (
    <Link to="/" className="nav-exit" style={{ padding: 0 }}>
      {t('sandbox.back')}
    </Link>
  )
}

/** Remembers the last console page, for "Back to the console" from settings. */
export function useLastConsolePage(href: string, settings: boolean): string | undefined {
  const [last, setLast] = useState<string>()
  useEffect(() => {
    if (!settings) setLast(href)
  }, [href, settings])
  return last
}
